import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "@/hooks/useTranslation";
import { bindPurchase, getPurchases, type Purchase } from "@/services/api/purchases";
import type { PlanType } from "@/models/Plan";
import { planRank } from "@/utils/purchaseEvent";

interface UnassignedPurchaseApplyProps {
  eventUuid: string;
  eventName: string;
  currentPlan: PlanType;
  onApplied: () => void;
}

/**
 * JUB-157: legacy purchases paid before plans were tied to an event can be
 * applied once, to one event, after an explicit irreversible confirmation.
 * Only purchases that would raise this event's plan are offered.
 */
export function UnassignedPurchaseApply({ eventUuid, eventName, currentPlan, onApplied }: UnassignedPurchaseApplyProps) {
  const { t } = useTranslation();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  // The confirmation is a snapshot of (purchase, event): it never follows
  // navigation to another event (F11).
  const [pending, setPending] = useState<{ purchase: Purchase; eventUuid: string } | null>(null);
  const [chosenId, setChosenId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  // A failed check is shown with a retry instead of silently hiding a paid
  // plan the owner could apply (JUB-157 D2).
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setPending(null);
    setChosenId(null);
  }, [eventUuid]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    getPurchases()
      .then(({ items }) => {
        if (!cancelled) setPurchases(items.filter((p) => p.status === "unassigned"));
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => { cancelled = true; };
  }, [eventUuid, reloadKey]);

  const applicable = purchases
    .filter((p) => planRank(p.plan) > planRank(currentPlan))
    .sort((a, b) => planRank(b.plan) - planRank(a.plan));

  if (loadError && applicable.length === 0) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs" role="alert" data-testid="unassigned-purchases-error">
        <p>{t("purchases.loadError")}</p>
        <Button size="sm" variant="outline" className="mt-2 text-xs" onClick={() => setReloadKey((k) => k + 1)} data-testid="unassigned-purchases-retry">
          {t("common.retry")}
        </Button>
      </div>
    );
  }

  if (applicable.length === 0) {
    return null;
  }

  // Default to the highest plan; the owner may pick another one.
  const chosen = applicable.find((p) => p.id === chosenId) ?? applicable[0];
  const planName = (plan: string) => t(`landing.pricing.${plan}.name`) || plan;

  const handleConfirm = async () => {
    if (!pending || pending.eventUuid !== eventUuid) {
      setPending(null);
      return;
    }
    setBusy(true);
    try {
      await bindPurchase(pending.purchase.id, pending.eventUuid);
      toast.success(t("purchases.applied"));
      setPurchases((list) => list.filter((p) => p.id !== pending.purchase.id));
      setPending(null);
      onApplied();
    } catch {
      toast.error(t("purchases.applyError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-pink-200 bg-pink-50 p-3 text-xs dark:border-pink-900 dark:bg-pink-950/20" data-testid="unassigned-purchase-apply">
      <p className="font-medium">{t("purchases.applyTitle")}</p>
      <p className="mt-1 text-muted-foreground">{t("purchases.applyDescription", { plan: planName(chosen.plan) })}</p>
      {applicable.length > 1 && (
        <select
          className="mt-2 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
          value={chosen.id}
          onChange={(e) => setChosenId(Number(e.target.value))}
          aria-label={t("purchases.chooseLabel")}
          data-testid="unassigned-purchase-choice"
        >
          {applicable.map((p) => (
            <option key={p.id} value={p.id}>
              {planName(p.plan)}
              {p.createdAt ? ` · ${p.createdAt.slice(0, 10)}` : ""}
            </option>
          ))}
        </select>
      )}
      <Button
        size="sm"
        className="mt-2 text-xs"
        onClick={() => setPending({ purchase: chosen, eventUuid })}
        data-testid="apply-unassigned-purchase-button"
      >
        {t("purchases.applyAction")}
      </Button>

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent data-testid="apply-unassigned-purchase-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("purchases.confirmTitle", { plan: planName(pending?.purchase.plan ?? chosen.plan) })}</AlertDialogTitle>
            <AlertDialogDescription>{t("purchases.confirmDescription", { name: eventName })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} disabled={busy} data-testid="confirm-apply-unassigned-purchase">
              {busy ? t("common.loading") : t("purchases.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
